// The arithmetic behind /admin → Analytics and the Routes page's week strip.
//
// Pure and DOM-free, for the reason utils/adminExport.js is: every number here
// leaves the building — on a projector in front of university admin, or in a
// file handed to a café owner — and the failure all of them share is silence.
// A week that starts on Monday, a day with no events drawn as a straight line
// across a gap, a search term with a colon in it split into the wrong counter:
// each renders perfectly and is wrong. `src/analytics.test.js` pins them.
//
// ── Days are strings ────────────────────────────────────────────────────────
// Every store buckets its per-day hashes by the `America/Winnipeg` calendar
// date ("2026-09-09"). Those are compared as strings — ISO dates sort
// lexicographically — and only turned into a Date to name a weekday. A range is
// `{ from, to }` of the same strings, both inclusive, either one null for
// "open at that end".

// ── Calendar ────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** A local Date → "YYYY-MM-DD". Local, because the operator is in Winnipeg. */
export function dayOf(date) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * "YYYY-MM-DD" → a Date at LOCAL noon.
 *
 * Noon, not midnight: adding days across a daylight-saving change moves a
 * midnight Date to 23:00 or 01:00, and a 23:00 reads back as the day before.
 * Noon is twelve hours from either edge and survives any DST shift there is.
 */
export function dateOfDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
}

export function addDays(day, n) {
  const d = dateOfDay(day);
  if (!d) return day;
  d.setDate(d.getDate() + n);
  return dayOf(d);
}

/**
 * The SUNDAY that starts the week holding `dayOrTs`, as "YYYY-MM-DD".
 *
 * ⚠️ Sunday, not Monday, and not ISO weeks — the operator asked for "Sunday
 * August 30 – Saturday September 5", which is how a North American wall
 * calendar is printed and how a term's week is talked about here.
 */
export function weekOf(dayOrTs) {
  const d = typeof dayOrTs === 'string' ? dateOfDay(dayOrTs) : new Date(dayOrTs);
  if (!d || Number.isNaN(d.getTime())) return null;
  const noon = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
  noon.setDate(noon.getDate() - noon.getDay());
  return dayOf(noon);
}

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/**
 * A week, named.
 *   long:  "Sunday August 30 – Saturday September 5"
 *   short: "Aug 30 – Sep 5"
 * The year is added only when the week is not wholly in `thisYear`, so the
 * strip stays short and last December is still told apart from this one.
 */
export function weekLabel(weekKey, { long = false, thisYear = new Date().getFullYear() } = {}) {
  const a = dateOfDay(weekKey);
  if (!a) return String(weekKey || '');
  const b = dateOfDay(addDays(weekKey, 6));
  // Both ends carry the year if EITHER is outside this one, so a week that
  // straddles New Year reads "Dec 28, 2025 – Jan 3, 2026" rather than half-dated.
  const dated = a.getFullYear() !== thisYear || b.getFullYear() !== thisYear;
  const yr = (d) => (dated ? `, ${d.getFullYear()}` : '');
  if (long) {
    return `${WEEKDAY[a.getDay()]} ${MONTH[a.getMonth()]} ${a.getDate()}${yr(a)} – `
      + `${WEEKDAY[b.getDay()]} ${MONTH[b.getMonth()]} ${b.getDate()}${yr(b)}`;
  }
  const m = (d) => MONTH[d.getMonth()].slice(0, 3);
  return `${m(a)} ${a.getDate()}${yr(a)} – ${m(b)} ${b.getDate()}${yr(b)}`;
}

/**
 * Every week from the earliest of `weekKeys` to the week holding `today`,
 * NEWEST FIRST, with no gaps.
 *
 * No gaps is the point: a quiet reading week with no searches at all is a fact
 * worth seeing in the strip, and skipping it would put the week before and the
 * week after side by side as though they were consecutive.
 */
export function weekRange(weekKeys, today = dayOf(new Date())) {
  const keys = [...weekKeys].filter(Boolean).sort();
  const last = weekOf(today);
  if (!keys.length) return last ? [last] : [];
  const out = [];
  // Bounded, because this is a loop over a calendar: five years of weeks is far
  // past any retention here, and an odd key must not become an endless strip.
  for (let w = keys[0], i = 0; w <= last && i < 270; w = addDays(w, 7), i++) out.push(w);
  if (!out.length || out[out.length - 1] !== last) out.push(last);
  return out.reverse();
}

// ── Ranges ──────────────────────────────────────────────────────────────────

export const RANGE_PRESETS = [
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: 'term', label: 'This term' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
];

/**
 * The academic term holding `today`, by the University of Manitoba's calendar
 * shape: Fall is September–December, Winter January–April, Summer May–August.
 * Approximate on purpose — a term-to-date figure, not the registrar's dates.
 */
export function termRange(today = dayOf(new Date())) {
  const [y, m] = today.split('-').map(Number);
  const start = m >= 9 ? `${y}-09-01` : m >= 5 ? `${y}-05-01` : `${y}-01-01`;
  return { from: start, to: today };
}

/** A preset id → `{ from, to }`. `all` is open at both ends. */
export function rangeFromPreset(id, today = dayOf(new Date()), custom = {}) {
  if (id === 'all') return { from: null, to: null };
  if (id === 'term') return termRange(today);
  if (id === 'custom') return { from: custom.from || null, to: custom.to || null };
  const n = Math.max(1, Number(id) || 30);
  return { from: addDays(today, -(n - 1)), to: today };
}

export const inRange = (day, r = {}) => !!day && (!r.from || day >= r.from) && (!r.to || day <= r.to);

/** Whether a row's timestamp falls in the range, by its LOCAL calendar day. */
export const tsInRange = (ts, r = {}) => !!ts && inRange(dayOf(new Date(ts)), r);

/** "Sep 1, 2026 – Sep 21, 2026", or "All time". For a heading or a filename. */
export function rangeLabel(r = {}) {
  const f = (day) => {
    const d = dateOfDay(day);
    return d ? `${MONTH[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}` : '';
  };
  if (!r.from && !r.to) return 'All time';
  if (!r.from) return `Up to ${f(r.to)}`;
  if (!r.to) return `Since ${f(r.from)}`;
  return `${f(r.from)} – ${f(r.to)}`;
}

/** A range for a filename: "2026-09-01_2026-09-21" or "all-time". */
export const rangeSlug = (r = {}) => (!r.from && !r.to ? 'all-time' : `${r.from || 'start'}_${r.to || 'now'}`);

// ── Per-day rollups ─────────────────────────────────────────────────────────

/** The days of a newest-first store inside the range, OLDEST FIRST. */
export function daysIn(days, range) {
  return (days || []).filter((d) => inRange(d.day, range)).sort((a, b) => a.day.localeCompare(b.day));
}

export const sumField = (days, field) => (days || []).reduce((n, d) => n + (Number(d[field]) || 0), 0);

/**
 * One field as chart points, oldest first, WITH THE EMPTY DAYS FILLED IN.
 *
 * ⚠️ The stores only write a day that had an event in it, so a raw series has
 * holes — and a line drawn through a hole joins Friday to the next Tuesday as a
 * straight slope, which reads as a steady decline across a weekend that was
 * actually two zeroes. Filled from the range's start (or the first recorded
 * day) to its end (or the last recorded day).
 */
export function seriesOf(days, field, range = {}) {
  const inR = daysIn(days, range);
  if (!inR.length && !(range.from && range.to)) return [];
  const byDay = new Map(inR.map((d) => [d.day, typeof field === 'function' ? field(d) : Number(d[field]) || 0]));
  const start = range.from || inR[0].day;
  const end = range.to || inR[inR.length - 1].day;
  const out = [];
  for (let day = start, i = 0; day <= end && i < 2000; day = addDays(day, 1), i++) {
    out.push({ day, value: byDay.get(day) || 0 });
  }
  return out;
}

/** Per-day points folded into Sunday–Saturday weeks, oldest first. */
export function weeklyOf(points) {
  const byWeek = new Map();
  for (const p of points || []) {
    const w = weekOf(p.day);
    if (w) byWeek.set(w, (byWeek.get(w) || 0) + (Number(p.value) || 0));
  }
  return [...byWeek.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([week, value]) => ({ week, value }));
}

// ── Restaurants ─────────────────────────────────────────────────────────────
//
// The restaurant store keeps a business's figures in its PER-DAY hash rather
// than as an all-time tally (see api/_restaurantLog.js), which is what lets a
// range be answered at all. These fold those fields back out:
//
//   r:<rid>:<event>     one restaurant's count of one event
//   r:<rid>:visitors    distinct visitors to its page that day
//   i:<rid>:<itemId>    one dish opened
//   q:<rid>:<term>      a menu search inside one restaurant
//   q::<term>           a campus-wide dish search
//
// ⚠️ A SEARCH TERM MAY CONTAIN A COLON. Ids cannot (the server folds them), so
// the id is everything up to the second colon and the term is ALL the rest —
// splitting on every colon would file "pho: large" under a term called "pho".

export const RESTAURANT_EVENTS = [
  ['restaurant_view', 'Page views'],
  ['item_view', 'Dish views'],
  ['menu_search', 'Menu searches'],
  ['directions', 'Directions'],
  ['hours_open', 'Hours checked'],
  ['highlight_tap', 'Highlight taps'],
  ['review_open', 'Reviews opened'],
];

function splitField(field) {
  const a = field.indexOf(':');
  const b = a < 0 ? -1 : field.indexOf(':', a + 1);
  if (a < 0 || b < 0) return null;
  return { kind: field.slice(0, a), id: field.slice(a + 1, b), rest: field.slice(b + 1) };
}

const restaurantName = (names, id) => names?.restaurants?.[id] || id;

/**
 * One row per restaurant over the range, most-viewed first.
 *
 * `directionsPct` is directions per page view — "of the people who looked, how
 * many went" — which is the number that turns a page view into foot traffic,
 * and the one a business will ask about second.
 */
export function restaurantBreakdown(daily, names, range) {
  const byId = new Map();
  const row = (id) => {
    if (!byId.has(id)) {
      const r = { id, name: restaurantName(names, id), visitors: 0 };
      for (const [ev] of RESTAURANT_EVENTS) r[ev] = 0;
      byId.set(id, r);
    }
    return byId.get(id);
  };
  // Every restaurant ever named gets a row, even at zero: in a pitch, "nobody
  // opened it this month" is an answer, and a missing row reads as "not tracked".
  for (const id of Object.keys(names?.restaurants || {})) row(id);
  for (const d of daysIn(daily, range)) {
    for (const [field, v] of Object.entries(d)) {
      if (!field.startsWith('r:')) continue;
      const p = splitField(field);
      if (!p || !p.id) continue;
      const r = row(p.id);
      if (p.rest === 'visitors') r.visitors += Number(v) || 0;
      else if (p.rest in r) r[p.rest] += Number(v) || 0;
    }
  }
  return [...byId.values()]
    .map((r) => ({
      ...r,
      directionsPct: r.restaurant_view ? Math.round((r.directions / r.restaurant_view) * 1000) / 10 : null,
    }))
    .sort((a, b) => b.restaurant_view - a.restaurant_view || a.name.localeCompare(b.name));
}

/** Dishes opened over the range, most first. `rid` narrows to one restaurant. */
export function restaurantItems(daily, names, range, rid = null) {
  const counts = new Map();
  for (const d of daysIn(daily, range)) {
    for (const [field, v] of Object.entries(d)) {
      if (!field.startsWith('i:')) continue;
      const p = splitField(field);
      if (!p || (rid && p.id !== rid)) continue;
      const key = `${p.id}:${p.rest}`;
      counts.set(key, (counts.get(key) || 0) + (Number(v) || 0));
    }
  }
  return [...counts.entries()].map(([key, views]) => {
    const at = key.indexOf(':');
    const id = key.slice(0, at);
    const itemId = key.slice(at + 1);
    return {
      restaurantId: id, restaurant: restaurantName(names, id),
      itemId, item: names?.items?.[key] || itemId, views,
    };
  }).sort((a, b) => b.views - a.views || a.item.localeCompare(b.item));
}

/**
 * What people typed, most first.
 *
 * `scope`: 'all' (both kinds), 'campus' (the every-menu dish search only), or a
 * restaurant id (searches inside that one menu). A campus-wide search is about
 * no restaurant and is never credited to one — "what are people looking for
 * that nobody sells" is a campus question.
 */
export function restaurantQueries(daily, names, range, scope = 'all') {
  const counts = new Map();
  for (const d of daysIn(daily, range)) {
    for (const [field, v] of Object.entries(d)) {
      if (!field.startsWith('q:')) continue;
      const p = splitField(field);
      if (!p || !p.rest) continue;
      if (scope === 'campus' && p.id) continue;
      if (scope !== 'all' && scope !== 'campus' && p.id !== scope) continue;
      const key = `${p.id}\u0000${p.rest}`;
      counts.set(key, (counts.get(key) || 0) + (Number(v) || 0));
    }
  }
  return [...counts.entries()].map(([key, count]) => {
    const [id, query] = key.split('\u0000');
    return { query, count, restaurantId: id || null, where: id ? restaurantName(names, id) : 'All menus (dish search)' };
  }).sort((a, b) => b.count - a.count || a.query.localeCompare(b.query));
}

/** One restaurant, or the campus totals, day by day — oldest first, gaps filled. */
export function restaurantDays(daily, range, rid = null) {
  const inR = daysIn(daily, range);
  if (!inR.length) return [];
  const start = range?.from || inR[0].day;
  const end = range?.to || inR[inR.length - 1].day;
  const byDay = new Map(inR.map((d) => [d.day, d]));
  const out = [];
  for (let day = start, i = 0; day <= end && i < 2000; day = addDays(day, 1), i++) {
    const d = byDay.get(day) || {};
    const r = { day };
    if (rid) {
      r.visitors = Number(d[`r:${rid}:visitors`]) || 0;
      for (const [ev] of RESTAURANT_EVENTS) r[ev] = Number(d[`r:${rid}:${ev}`]) || 0;
    } else {
      r.sessions = Number(d.sessions) || 0;
      r.events = Number(d.events) || 0;
      for (const [ev] of RESTAURANT_EVENTS) r[ev] = Number(d[`ev:${ev}`]) || 0;
      r.dish_search = Number(d['ev:dish_search']) || 0;
      r.ios = Number(d.ios) || 0;
      r.android = Number(d.android) || 0;
      r.web = Number(d.web) || 0;
    }
    out.push(r);
  }
  return out;
}

// ── Downloadable datasets ───────────────────────────────────────────────────
//
// ── App downloads since the store release ───────────────────────────────────

/**
 * The day UMaps went on the App Store and Google Play — 2026-10-05, Winnipeg
 * time, which is how the stores stamp their days (`dayStamp` in
 * api/_appProfile.js). Every beta phone had to download the app afresh that
 * day, so a count that starts here is "downloads of the released app", and one
 * that starts earlier is the beta testers plus the same people again.
 */
export const APP_RELEASE_DAY = '2026-10-05';

/**
 * Downloads of the released app, folded from the questionnaire's per-day
 * rollups (`appProfileDaily`).
 *
 * A download is a DISTINCT PHONE whose first-run answers arrived that day
 * (`installs`, see api/_appProfile.js) — not the stores' own figure, which they
 * keep to themselves, and not a phone that turned analytics off in Settings.
 * `ios` / `android` are the `installs:<platform>` split, which the server only
 * started writing on the release day; `unknown` is whatever `installs` holds
 * that the split does not (a phone that sent no platform, or the hours before
 * the split was deployed), kept rather than dropped so the three always add up
 * to the total.
 *
 * `series` is filled from the release day to `today` with zeroes, so the line
 * says "nobody downloaded it on Sunday" rather than skipping Sunday; `rows` is
 * the same days NEWEST FIRST with a running total, for the table.
 */
export function downloadsSince(daily, today = dayOf(new Date()), from = APP_RELEASE_DAY) {
  const to = today >= from ? today : from;
  const range = { from, to };
  const days = daysIn(daily, range);
  const byDay = new Map(days.map((d) => [d.day, d]));
  const total = sumField(days, 'installs');
  const ios = sumField(days, 'installs:ios');
  const android = sumField(days, 'installs:android');
  const series = seriesOf(days, 'installs', range);
  let best = { day: null, value: 0 };
  let running = 0;
  const rows = series.map((p) => {
    const d = byDay.get(p.day) || {};
    running += p.value;
    if (p.value > best.value) best = { day: p.day, value: p.value };
    return {
      day: p.day, value: p.value, running,
      ios: Number(d['installs:ios']) || 0,
      android: Number(d['installs:android']) || 0,
    };
  });
  return {
    from, to, total, ios, android,
    unknown: Math.max(0, total - ios - android),
    today: Number(byDay.get(today)?.installs) || 0,
    dayCount: series.length,
    best, series,
    rows: rows.slice().reverse(),
  };
}

// What the Analytics tab's "Download data" panel offers. Each builds ONE table
// — `{ header, rows }` — from the dashboard payload plus the restaurant stats,
// for a range, so the same definition serves the CSV and the JSON and they
// cannot disagree. `span` says what the range can do to it:
//   'daily' — built from per-day rollups that never expire; the range is exact.
//   'rows'  — built from a capped, rolling row list; the range filters what is
//             still held, and the file says how far back that is.
//   'total' — an all-time tally with no dates in it; the range does not apply,
//             and the panel says so rather than pretending to filter it.

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtTs = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : `${dayOf(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const DATASETS = [
  {
    key: 'searches-daily', group: 'Campus map', label: 'Route searches per day', span: 'daily',
    build: ({ data }, r) => ({
      header: ['day', 'sessions', 'searches', 'routed', 'failed', 'accessible', 'accessible_failed',
        'tunnels', 'stairs_only', 'course_mode', 'desktop', 'mobile_web', 'app_ios', 'app_android', 'returning'],
      rows: daysIn(data?.daily, r).map((d) => [
        d.day, n(d.sessions), n(d.searches), n(d.found), n(d.failed), n(d['opt:accessible']),
        n(d.accessibleFailed), n(d['opt:tunnels']), n(d['opt:noElevators']), n(d['opt:course']),
        n(d.desktop), n(d.mobile), n(d.ios), n(d.android), n(d.returning),
      ]),
    }),
  },
  {
    key: 'searches-rows', group: 'Campus map', label: 'Route searches (every search)', span: 'rows',
    needsAllRoutes: true,
    build: ({ data }, r) => ({
      header: ['when', 'from', 'to_building', 'to_room', 'found', 'meters', 'accessible', 'tunnels',
        'device', 'returning'],
      rows: (data?.routes || []).filter((x) => tsInRange(x.ts, r)).map((x) => [
        fmtTs(x.ts), x.from?.text || '', x.to?.building || '', x.to?.room || '',
        x.found ? 'yes' : 'no', x.meters == null ? '' : n(x.meters),
        x.options?.accessible ? 'yes' : 'no', x.options?.tunnels ? 'yes' : 'no',
        x.device || '', x.returning ? 'yes' : 'no',
      ]),
    }),
  },
  {
    key: 'nearby-daily', group: 'Campus map', label: 'Nearby panel per day', span: 'daily',
    build: ({ data }, r) => ({
      header: ['day', 'sessions', 'panel_opens', 'place_views', 'directions', 'found_nothing',
        'study', 'library', 'washroom', 'bus_stop', 'microwave', 'restaurant'],
      rows: daysIn(data?.nearby?.daily, r).map((d) => [
        d.day, n(d.sessions), n(d['ev:open']), n(d['ev:view']), n(d['ev:go']), n(d['ev:empty']),
        n(d['kind:study']), n(d['kind:library']), n(d['kind:bathroom']), n(d['kind:busstop']),
        n(d['kind:amenity:microwave']), n(d['kind:restaurant']),
      ]),
    }),
  },
  {
    key: 'nearby-places', group: 'Campus map', label: 'Most-looked-at places', span: 'total',
    build: ({ data }) => ({
      header: ['kind', 'place', 'views', 'directions', 'place_id'],
      rows: (data?.nearby?.places || []).map((p) => [p.kind || '', p.name || '', n(p.views), n(p.gos), p.placeId || '']),
    }),
  },
  {
    key: 'events-daily', group: 'Campus map', label: 'Club events per day', span: 'daily',
    build: ({ data }, r) => ({
      header: ['day', 'sessions', 'opens', 'from_pin', 'from_list', 'directions', 'starred',
        'unstarred', 'tickets', 'links', 'club_pages'],
      rows: daysIn(data?.eventUse?.daily, r).map((d) => [
        d.day, n(d.sessions), n(d.opens), n(d['ev:pin']), n(d['ev:row']), n(d['ev:directions']),
        n(d['ev:save']), n(d['ev:unsave']), n(d['ev:tickets']), n(d['ev:link']), n(d['ev:club']),
      ]),
    }),
  },
  {
    key: 'app-daily', group: 'Phone app', label: 'App signups per day', span: 'daily',
    build: ({ data }, r) => ({
      header: ['day', 'new_installs', 'answers', 'ios', 'android'],
      rows: daysIn(data?.appProfileDaily, r).map((d) => [d.day, n(d.installs), n(d.answers), n(d.ios), n(d.android)]),
    }),
  },
  {
    // Starts on the release day whatever the range says: the days before it
    // are the beta, and "app downloads" is a claim about the released app.
    key: 'app-downloads', group: 'Phone app', label: 'App downloads since release (per day)', span: 'daily',
    build: ({ data }, r) => ({
      header: ['day', 'downloads', 'iphone', 'android', 'running_total'],
      rows: downloadsSince(
        data?.appProfileDaily,
        r.to || dayOf(new Date()),
        r.from && r.from > APP_RELEASE_DAY ? r.from : APP_RELEASE_DAY,
      ).rows.slice().reverse().map((d) => [d.day, d.value, d.ios, d.android, d.running]),
    }),
  },
  {
    key: 'ratings', group: 'Phone app', label: 'Route ratings', span: 'rows',
    build: ({ data }, r) => ({
      header: ['when', 'verdict', 'from', 'to', 'meters', 'accessible', 'device', 'note'],
      rows: (data?.routeRatings || []).filter((x) => tsInRange(x.ts, r)).map((x) => [
        fmtTs(x.ts), x.verdict || '', x.from || '',
        `${x.to?.name || x.to?.building || ''} ${x.to?.room || ''}`.trim(),
        x.meters == null ? '' : n(x.meters), x.options?.accessible ? 'yes' : 'no', x.device || '', x.note || '',
      ]),
    }),
  },
  {
    key: 'restaurants-daily', group: 'Restaurants', label: 'Restaurant activity per day (all)', span: 'daily',
    build: ({ restaurants }, r) => {
      const rows = restaurantDays(restaurants?.daily, r);
      return {
        header: ['day', 'sessions', 'events', ...RESTAURANT_EVENTS.map(([k]) => k), 'dish_search', 'ios', 'android', 'web'],
        rows: rows.map((d) => [d.day, d.sessions, d.events, ...RESTAURANT_EVENTS.map(([k]) => d[k]),
          d.dish_search, d.ios, d.android, d.web]),
      };
    },
  },
  {
    key: 'restaurants-breakdown', group: 'Restaurants', label: 'Per-restaurant breakdown', span: 'daily',
    build: ({ restaurants }, r) => ({
      header: ['restaurant', 'visitors', ...RESTAURANT_EVENTS.map(([k]) => k), 'directions_per_view_pct', 'restaurant_id'],
      rows: restaurantBreakdown(restaurants?.daily, restaurants?.names, r).map((x) => [
        x.name, x.visitors, ...RESTAURANT_EVENTS.map(([k]) => x[k]),
        x.directionsPct == null ? '' : x.directionsPct, x.id,
      ]),
    }),
  },
  {
    key: 'restaurants-items', group: 'Restaurants', label: 'Dishes opened', span: 'daily',
    build: ({ restaurants }, r) => ({
      header: ['restaurant', 'dish', 'views', 'restaurant_id', 'item_id'],
      rows: restaurantItems(restaurants?.daily, restaurants?.names, r)
        .map((x) => [x.restaurant, x.item, x.views, x.restaurantId, x.itemId]),
    }),
  },
  {
    key: 'restaurants-searches', group: 'Restaurants', label: 'Food search terms', span: 'daily',
    build: ({ restaurants }, r) => ({
      header: ['search', 'times', 'where'],
      rows: restaurantQueries(restaurants?.daily, restaurants?.names, r).map((x) => [x.query, x.count, x.where]),
    }),
  },
  {
    // The pitch. One business's own numbers, and nobody else's — which is what
    // makes it something that can be sent to them. Needs `opts.restaurantId`.
    key: 'restaurant-one', group: 'Restaurants', label: 'One restaurant, day by day', span: 'daily',
    needsRestaurant: true,
    build: ({ restaurants }, r, opts = {}) => ({
      header: ['day', 'visitors', ...RESTAURANT_EVENTS.map(([k]) => k)],
      rows: opts.restaurantId
        ? restaurantDays(restaurants?.daily, r, opts.restaurantId)
          .map((d) => [d.day, d.visitors, ...RESTAURANT_EVENTS.map(([k]) => d[k])])
        : [],
    }),
  },
];

export const datasetFor = (key) => DATASETS.find((d) => d.key === key) || null;

/** A table → an array of plain objects, which is what a JSON download holds. */
export function tableToObjects({ header, rows }) {
  return (rows || []).map((row) => Object.fromEntries(header.map((h, i) => [h, row[i] ?? null])));
}

/**
 * One restaurant's whole report as several sheets — the per-business pitch as
 * a workbook. Its figures only: summary, day by day, dishes, the searches made
 * inside its own menu. Campus-wide dish searches are deliberately NOT in it;
 * they are about no business, and a report sent to one café must not carry
 * what students were looking for at the one next door.
 */
export function restaurantReport(restaurants, range, rid) {
  const all = restaurantBreakdown(restaurants?.daily, restaurants?.names, range);
  const me = all.find((x) => x.id === rid);
  if (!me) return null;
  const rank = all.filter((x) => x.restaurant_view > 0).findIndex((x) => x.id === rid) + 1;
  const days = restaurantDays(restaurants?.daily, range, rid);
  return {
    name: me.name,
    sheets: [
      {
        name: 'Summary',
        header: ['measure', 'value'],
        rows: [
          ['Restaurant', me.name],
          ['Period', rangeLabel(range)],
          ['Distinct visitors (sum of daily)', me.visitors],
          ...RESTAURANT_EVENTS.map(([k, label]) => [label, me[k]]),
          ['Directions per page view (%)', me.directionsPct == null ? '' : me.directionsPct],
          ['Rank by page views on campus', rank > 0 ? `${rank} of ${all.filter((x) => x.restaurant_view > 0).length}` : ''],
        ],
      },
      {
        name: 'By day',
        header: ['day', 'visitors', ...RESTAURANT_EVENTS.map(([k]) => k)],
        rows: days.map((d) => [d.day, d.visitors, ...RESTAURANT_EVENTS.map(([k]) => d[k])]),
      },
      {
        name: 'Dishes',
        header: ['dish', 'views'],
        rows: restaurantItems(restaurants?.daily, restaurants?.names, range, rid).map((x) => [x.item, x.views]),
      },
      {
        name: 'Menu searches',
        header: ['search', 'times'],
        rows: restaurantQueries(restaurants?.daily, restaurants?.names, range, rid).map((x) => [x.query, x.count]),
      },
    ],
  };
}
